<template>
  <div
    class="filters-list"
    v-if="excludedFromFiltersList && list.length > 0 && !hasAllDisabledFilters"
  >
    <div class="filters-list__accordion-intro" @click="toggleAccordion(slug)">
      <div class="filters-list__toggle-container">
        <p v-if="slug" class="filters-list__toggle text-medium2">
          {{ checkTranslate(slug) }}
        </p>
        <span
          v-if="selectedCount > 0"
          class="filters-list__count text-base is-bold"
        >
          ({{ selectedCount }})
        </span>
      </div>
      <img
        :class="{ 'rotate-icon': isOpen[slug] }"
        src="@/assets/img/ico/arrow-mobile.svg?url"
        alt="Icon"
      />
    </div>
    <div
      ref="accordionContent"
      :class="[
        'filters-list__content',
        { 'is-open': isOpen[slug] || isMobile },
      ]"
    >
      <ul>
        <li v-for="(filter, index) in orderedList" :key="index">
          <FiltersButton
            :filter="filter"
            :selected="filter.selected"
            :forceDisabled="
              (orderedList.length < 1 && !filter.selected) || filter.disabled
            "
            @select:filter="onSelect(filter.value)"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { gsap } from 'gsap'

// Define the event to emit filter updates
const emit = defineEmits(['update:filter'])

const { t, te } = useI18n()

// Define props with default values
const props = defineProps({
  slug: String,
  filters: Array,
  selected: Array,
})

// Define reactive variables using `toRefs` for props
const { slug, filters, selected } = toRefs(props)

// Reactive
const list = computed(() => filters.value || [])
const selectedNow = ref(selected.value || [])
const isOpen = ref({}) // Accordion state for each slug
const accordionContent = ref(null)

// Mobile breakpoint
const isMobile = ref(false)

onMounted(() => {
  isMobile.value = window.innerWidth <= 1000
  if (isMobile.value && accordionContent.value) {
    openAllAccordions()
  }
})

// Function to open all accordions
const openAllAccordions = () => {
  list.value.forEach((filter) => {
    isOpen.value[filter.slug] = true
  })
  const content = accordionContent.value
  if (content) {
    gsap.to(content, {
      autoAlpha: 1,
      height: 'auto',
      duration: 0.5,
      ease: 'power2.out',
    })
  }
}

// Check if there are only disabled filters
const hasAllDisabledFilters = computed(() => {
  return orderedList.value.every((filter) => {
    const forceDisabled =
      (orderedList.value.length < 1 && !filter.selected) || filter.disabled
    return forceDisabled
  })
})

// Function to handle accordion toggle
const toggleAccordion = (slug) => {
  isOpen.value = { ...isOpen.value, [slug]: !isOpen.value[slug] }
  const content = accordionContent.value

  if (isOpen.value[slug]) {
    gsap.to(content, {
      autoAlpha: 1,
      height: 'auto',
      duration: 0.5,
      ease: 'power2.out',
    })
  } else {
    gsap.to(content, {
      autoAlpha: 0,
      height: 0,
      duration: 0.5,
      ease: 'power2.inOut',
    })
  }
}

// List that orders selected filters first
const orderedList = computed(() => {
  return list.value
    .map((filter) => ({
      ...filter,
      selected: selectedNow.value.includes(filter.value),
      count: !checkIfAvailable(filter) ? 0 : filter.count,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

// Watch for changes in selected filters
watch(
  () => selected.value,
  (newSelected, oldSelected) => {
    if (newSelected.length <= 0 && oldSelected.length > 0) {
      selectedNow.value = []
      // Close all accordions when filters are reset, in desktop
      if (!isMobile.value) {
        Object.keys(isOpen.value).forEach((key) => {
          toggleAccordion(key)
        })
      }
    }
  },
)

// Function to check if a filter is available
const checkIfAvailable = (item) => {
  return list.value.some(
    (elementToCheck) => elementToCheck.value === item.value,
  )
}

// Function to handle filter selection
const onSelect = (value) => {
  if (selectedNow.value.includes(value)) {
    selectedNow.value = selectedNow.value.filter(
      (selected) => selected !== value,
    )
  } else {
    selectedNow.value = [...selectedNow.value, value]
  }

  // Emit an event with the updated filters
  emit('update:filter', {
    [slug.value]: selectedNow.value,
  })
}

// Function to translate the filter `slug`
const checkTranslate = (slug) => {
  return te(`filters['${slug}']`) ? t(`filters['${slug}']`) : slug
}

// Computed property to count selected filters
const selectedCount = computed(() => selectedNow.value.length)

// Excluded filters
const excludedFromFiltersList = computed(() => {
  const filterSlugs = [
    'Filtro_Categoria_Lievito_Vino',
    'Filtro_Categoria_Prodotto_Vino',
    'Filtro_Categoria_Lievito_Birra',
  ]
  return !filterSlugs.includes(props.slug)
})
</script>

<script>
export default {
  name: 'FiltersList',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
